import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface OrganizationManagerGuardProps {
  children: React.ReactNode;
}

export const OrganizationManagerGuard = ({ children }: OrganizationManagerGuardProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkManagerAccess = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          console.log("[OrganizationManagerGuard] No session, redirecting to auth");
          navigate("/auth", { replace: true });
          return;
        }

        // Check if user is super_admin (they can access all organizations)
        const { data: isSuperAdmin, error: superAdminError } = await supabase
          .rpc("is_super_admin", { _user_id: session.user.id });

        if (!superAdminError && isSuperAdmin) {
          console.log("[OrganizationManagerGuard] Super admin access granted");
          setIsAuthorized(true);
          setIsLoading(false);
          return;
        }

        // Check if user has manager role
        const { data: hasManagerRole, error: roleError } = await supabase
          .rpc("has_manager_role", { _user_id: session.user.id });

        if (roleError) {
          console.error("[OrganizationManagerGuard] Error checking role:", roleError);
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        if (!hasManagerRole) {
          console.log("[OrganizationManagerGuard] User is not a manager");
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        // Check if user has organization_id in profile
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("organization_id")
          .eq("id", session.user.id)
          .single();

        if (profileError || !profile?.organization_id) {
          console.error("[OrganizationManagerGuard] No organization_id found:", profileError);
          setIsAuthorized(false);
          setIsLoading(false);
          return;
        }

        console.log("[OrganizationManagerGuard] Access authorized");
        setIsAuthorized(true);
      } catch (error) {
        console.error("[OrganizationManagerGuard] Error:", error);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkManagerAccess();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        navigate("/auth", { replace: true });
      } else if (event === "SIGNED_IN") {
        checkManagerAccess();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Alert className="max-w-md">
          <AlertDescription className="space-y-4">
            <p className="font-medium">Accès non autorisé</p>
            <p className="text-sm text-muted-foreground">
              Vous n'avez pas accès à la vue organisation. Seuls les managers peuvent accéder à cette page.
            </p>
            <Button 
              onClick={() => navigate("/auth")}
              variant="outline"
            >
              Retour à la connexion
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
};
